import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  Star,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignIn from '../components/GoogleSignIn';
import LoginLoading from '../components/LoginLoading';
import LogoIcon from '../components/LogoIcon';

const EASE = [0.22, 1, 0.36, 1];

/* Left Showcase Panel with HD Looping Video + Pop-up Badges */
const LeftShowcase = () => (
  <div className="hidden lg:flex w-1/2 flex-col justify-between bg-[#111827] text-white p-10 relative overflow-hidden">
    {/* Top Brand Header */}
    <div className="relative z-20 flex items-center gap-3">
      <LogoIcon className="h-8 w-auto text-white" />
      <span className="text-base font-bold tracking-tight text-white">AI Digital Twin</span>
    </div>

    {/* Center Video Container with Animated Floating Pop-ups */}
    <div className="relative z-10 my-auto max-w-lg mx-auto w-full">
      <div className="relative rounded-2xl border border-white/15 overflow-hidden shadow-2xl group">
        {/* Live Looping HD Video of Woman with Smartphone */}
        <video
          autoPlay
          loop
          muted
          playsInline
          poster="/assets/images/woman_phone.jpg"
          className="w-full h-[460px] object-cover object-center rounded-2xl transform transition-transform duration-700 group-hover:scale-105"
        >
          <source
            src="https://assets.mixkit.co/videos/preview/mixkit-woman-holding-a-smartphone-in-a-modern-office-41566-large.mp4"
            type="video/mp4"
          />
          <source
            src="https://assets.mixkit.co/videos/preview/mixkit-young-woman-talking-on-the-phone-and-using-a-laptop-42861-large.mp4"
            type="video/mp4"
          />
        </video>

        {/* Gradient Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-[#111827]/30 to-transparent pointer-events-none" />

        {/* Floating Pop-up Badge 1 (Top-Left) */}
        <motion.div
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="absolute top-4 left-4 z-20 flex items-center gap-2 rounded-full border border-white/20 bg-slate-900/85 px-3.5 py-1.5 backdrop-blur-md shadow-lg"
        >
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[11px] font-semibold text-white">LIVE DEMO • AI Twin Active</span>
        </motion.div>

        {/* Floating Pop-up Badge 2 (Top-Right) */}
        <motion.div
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="absolute top-4 right-4 z-20 flex items-center gap-2 rounded-full border border-white/20 bg-slate-900/85 px-3.5 py-1.5 backdrop-blur-md shadow-lg"
        >
          <span className="text-[11px] font-semibold text-emerald-300">0.4s Reply Speed</span>
        </motion.div>

        {/* Bottom Pop-up Content Overlay */}
        <div className="absolute bottom-0 inset-x-0 p-5 z-20">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="rounded-xl border border-white/20 bg-slate-900/90 p-4 backdrop-blur-md shadow-xl"
          >
            <p className="text-xs sm:text-sm font-bold text-white leading-snug">
              "My AI twin handles 100+ WhatsApp messages & calls every day in my exact voice."
            </p>
            <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-2">
              <span className="text-[11px] text-slate-300 font-medium">Answering customers 24/7</span>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>

    {/* Footer */}
    <div className="relative z-20 text-xs text-slate-500">
      © {new Date().getFullYear()} AI Digital Twin. All rights reserved.
    </div>
  </div>
);

/* Main Login Form Page */
const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { user, loading, login, setAuthData } = useAuth();
  const shakeRef = useRef(null);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Sign in — AI Digital Twin';
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    if (!loading && user) navigate('/dashboard');
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    try {
      setIsSubmitting(true);
      const result = await login(formData.email, formData.password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Login failed');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen bg-[#FAFAF7] text-[#111827] font-sans antialiased">
      <LoginLoading open={isSubmitting} />

      {/* Left Showcase Side */}
      <LeftShowcase />

      {/* Right Form Side */}
      <div className="flex w-full flex-col lg:w-1/2 bg-[#FAFAF7]">
        {/* Top Header */}
        <div className="flex items-center justify-between px-6 pt-6 sm:px-12 sm:pt-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-xs font-semibold text-[#4B5563] hover:text-[#111827] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </button>
          <div className="flex items-center gap-2 lg:hidden">
            <LogoIcon className="h-6 w-auto text-[#111827]" />
            <span className="text-sm font-bold tracking-tight text-[#111827]">AI Digital Twin</span>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl border border-[#E5E7EB] shadow-sm"
          >
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-1 text-[11px] font-semibold text-[#374151]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#111827]" />
                Secure Portal
              </span>
              <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-[#111827]">
                Welcome back
              </h1>
              <p className="mt-1.5 text-xs sm:text-sm text-[#6B7280]">
                Sign in to manage your AI digital twin and review customer chats.
              </p>
            </div>

            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  ref={shakeRef}
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-medium text-rose-700 flex items-center gap-2"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-600 flex-shrink-0" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#374151] mb-1.5">Email address</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="name@company.com"
                  className="w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm font-medium text-[#111827] outline-none transition-all focus:border-[#111827] focus:ring-1 focus:ring-[#111827] placeholder:text-slate-400"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-[#374151]">Password</label>
                  <RouterLink to="/forgot-password" className="text-xs font-semibold text-[#111827] hover:underline">
                    Forgot password?
                  </RouterLink>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm font-medium text-[#111827] outline-none transition-all focus:border-[#111827] focus:ring-1 focus:ring-[#111827] placeholder:text-slate-400 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#111827] p-1"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="remember"
                  className="h-4 w-4 rounded border-[#E5E7EB] text-[#111827] focus:ring-[#111827]"
                />
                <label htmlFor="remember" className="text-xs text-[#4B5563] cursor-pointer">
                  Remember this device
                </label>
              </div>

              <button
                type="submit"
                className="w-full mt-2 flex items-center justify-center gap-2 rounded-xl bg-[#111827] hover:bg-[#1F2937] py-3.5 text-sm font-semibold text-white shadow-sm transition-colors"
              >
                Sign in
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            {/* Divider */}
            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-[#E5E7EB]" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">or continue with</span>
              <div className="h-px flex-1 bg-[#E5E7EB]" />
            </div>

            <GoogleSignIn
              onLoginStart={() => setIsSubmitting(true)}
              onLoginSuccess={(data) => {
                setAuthData(data);
                navigate('/dashboard');
              }}
              onLoginError={(err) => {
                setError(err);
                setIsSubmitting(false);
              }}
            />

            <p className="mt-8 text-center text-xs font-medium text-[#6B7280]">
              Don't have an account?{' '}
              <RouterLink to="/register" className="font-bold text-[#111827] hover:underline">
                Create one free
              </RouterLink>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
